import { createAsyncThunk } from "@reduxjs/toolkit";
import customFetch from "../../util/config/axiosConfig";
import { getDataUserId } from "../../util/manageAuth";
import {
  MediaItem,
  StudioFormat,
  StudioTemplate,
  StudioUploadsResponse,
  StudioVoiceover,
  StudioVoiceoverUploadResponse,
} from "../../types/studioTypes";
import { FetchStudioMusicResponse, StudioMusicTrack, UploadStudioMusicParams, UploadStudioMusicResponse } from "../../types/videoType";

export const getUserId = () => {
  const userId = getDataUserId();

  if (!userId) {
    throw new Error("User not authenticated");
  }

  return Number(userId);
};

const getErrorMessage = (error: any, fallback: string): string => {
  return (
    error?.response?.data?.error?.message ||
    error?.response?.data?.message ||
    error?.message ||
    fallback
  );
};

const createFormData = (fields: Record<string, any>): FormData => {
  const formData = new FormData();

  Object.entries(fields).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item?.blob) {
          formData.append(key, item.blob, item.name);
        } else {
          formData.append(key, item);
        }
      });
    } else if (value?.blob) {
      formData.append(key, value.blob, value.name);
    } else {
      formData.append(key, value);
    }
  });

  return formData;
};

// ============================================================
// FORMATS & TEMPLATES
// ============================================================

export const fetchStudioFormats = createAsyncThunk<
  StudioFormat[],
  void,
  { rejectValue: string }
>("studio/fetchFormats", async (_, thunkAPI) => {
  try {
    const response = await customFetch.get("/api/studio/formats");

    let payload = response.data;

    if (typeof payload?.data === "string") {
      payload.data = JSON.parse(payload.data);
    }

    return payload.data as StudioFormat[];
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error?.message || "Failed to fetch studio formats"
    );
  }
});

export const fetchStudioTemplates = createAsyncThunk<
  StudioTemplate[],
  string | undefined, // optional category
  { rejectValue: string }
>("studio/fetchTemplates", async (category, thunkAPI) => {
  try {
    const response = await customFetch.get("/api/studio/templates", {
      params: category ? { category } : undefined,
    });

    let payload = response.data;

    if (typeof payload?.data === "string") {
      payload.data = JSON.parse(payload.data);
    }

    return payload.data as StudioTemplate[];
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      error?.message || "Failed to fetch studio templates"
    );
  }
});

// ============================================================
// DESIGNS
// ============================================================

export interface FetchStudioDesignsParams {
  page?: number;
  size?: number;
}

export const fetchStudioDesigns = createAsyncThunk<
  any,
  FetchStudioDesignsParams | undefined,
  { rejectValue: string }
>("studio/fetchDesigns", async (params, thunkAPI) => {
  try {
    const page = params?.page ?? 0;
    const size = params?.size ?? 24;

    const response = await customFetch.get("/api/studio/designs", {
      params: { page, size },
    });

    let payload = response.data;

    if (typeof payload?.data === "string") {
      payload.data = JSON.parse(payload.data);
    }

    return payload.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      getErrorMessage(error, "Failed to fetch studio designs")
    );
  }
});

export const createStudioDesign = createAsyncThunk<
  any,
  any,
  { rejectValue: string }
>("studio/createDesign", async (body, thunkAPI) => {
  try {
    const response = await customFetch.post("/api/studio/designs", body);
    return response.data?.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      getErrorMessage(error, "Failed to create design")
    );
  }
});

export const fetchStudioDesign = createAsyncThunk<
  any,
  number | string,
  { rejectValue: string }
>("studio/fetchDesign", async (id, thunkAPI) => {
  try {
    const response = await customFetch.get(`/api/studio/designs/${id}`);
    return response.data?.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      getErrorMessage(error, "Failed to fetch design")
    );
  }
});

export const saveStudioDesign = createAsyncThunk<
  any,
  { id: number | string; body: any },
  { rejectValue: string }
>("studio/saveDesign", async ({ id, body }, thunkAPI) => {
  try {
    const response = await customFetch.put(`/api/studio/designs/${id}`, body);
    return response.data?.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      getErrorMessage(error, "Failed to save design")
    );
  }
});

export const duplicateStudioDesign = createAsyncThunk<
  any,
  number | string,
  { rejectValue: string }
>("studio/duplicateDesign", async (id, thunkAPI) => {
  try {
    const response = await customFetch.post(
      `/api/studio/designs/${id}/duplicate`
    );
    return response.data?.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      getErrorMessage(error, "Failed to duplicate design")
    );
  }
});

export const deleteStudioDesign = createAsyncThunk<
  any,
  number | string,
  { rejectValue: string }
>("studio/deleteDesign", async (id, thunkAPI) => {
  try {
    const response = await customFetch.delete(`/api/studio/designs/${id}`);
    return response.data?.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      getErrorMessage(error, "Failed to delete design")
    );
  }
});

export interface ExportStudioDesignParams {
  id: number | string;
  blob: Blob;
  mode: string;
  templateName?: string;
}

export const exportStudioDesign = createAsyncThunk<
  any,
  ExportStudioDesignParams,
  { rejectValue: string }
>(
  "studio/exportDesign",
  async ({ id, blob, mode, templateName }, thunkAPI) => {
    try {
      const formData = new FormData();

      const fileName =
        blob.type === "image/png" ? "design.png" : "design.jpg";

      const file = new File([blob], fileName, {
        type: blob.type,
      });

      formData.append("file", file);
      formData.append("mode", mode);
      formData.append("templateName", templateName ?? "");
      
      const response = await customFetch.post(
        `/api/studio/designs/${id}/export`,
        formData,
        {
          headers: {
            "Content-Type": undefined,
          },
        }
      );

      return response.data?.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        getErrorMessage(error, "Failed to export design")
      );
    }
  }
);

// ============================================================
// UPLOADS (Gallery)
// ============================================================

export const uploadStudioAsset = createAsyncThunk<
  MediaItem,
  File,
  { rejectValue: string }
>("studio/uploadAsset", async (file: File, thunkAPI) => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await customFetch.post("/api/studio/uploads", formData, {
      headers: {
        "Content-Type": undefined,
      },
    });

    return response.data?.data as MediaItem;
  } catch (error: any) {
    console.error("Studio upload error:", error);
    console.error("Studio upload response:", error?.response?.data);

    return thunkAPI.rejectWithValue(
      getErrorMessage(error, "Failed to upload studio asset")
    );
  }
});

export interface FetchStudioUploadsParams {
  type?: string;
  page?: number;
  size?: number;
}

export const fetchStudioUploads = createAsyncThunk<
  StudioUploadsResponse,
  FetchStudioUploadsParams | undefined,
  { rejectValue: string }
>("studio/fetchUploads", async (params = {}, thunkAPI) => {
  const { type, page = 0, size = 30 } = params;
  try {
    const response = await customFetch.get("/api/studio/uploads", {
      params: {
        page,
        size,
        ...(type ? { type } : {}),
      },
    });

    return response.data?.data as StudioUploadsResponse;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      getErrorMessage(error, "Failed to fetch studio gallery assets")
    );
  }
});


// GET MUSIC
export const fetchStudioMusic = createAsyncThunk<
  StudioMusicTrack[],
  void,
  { rejectValue: string }
>(
  "studio/fetchMusic",
  async (_, thunkAPI) => {
    try {
      const response =
        await customFetch.get<FetchStudioMusicResponse>(
          "/api/studio/music"
        );

      const result = response.data;

      if (result.status !== 1) {
        return thunkAPI.rejectWithValue(
          result.error || "Failed to fetch music."
        );
      }

      return Array.isArray(result.data)
        ? result.data
        : [];
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to fetch music."
      );
    }
  }
);

export const uploadStudioMusic = createAsyncThunk<
  StudioMusicTrack,
  UploadStudioMusicParams,
  { rejectValue: string }
>(
  "studio/uploadMusic",
  async (
    { file, title, rightsConfirmed },
    thunkAPI
  ) => {
    try {
      const formData = new FormData();

      formData.append("file", file || "");

      const response =
        await customFetch.post<UploadStudioMusicResponse>(
          "/api/studio/music",
          formData,
          {
            params: {
              title,
              rightsConfirmed,
            },
            headers: {
              "Content-Type": undefined,
            },
          }
        );

      const result = response.data;

      if (result.status !== 1) {
        return thunkAPI.rejectWithValue(
          result.error || "Failed to upload music."
        );
      }

      return result.data;
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(
        error instanceof Error
          ? error.message
          : "Failed to upload music."
      );
    }
  }
);

export const deleteStudioMusic = createAsyncThunk<
  string,
  string | number,
  { rejectValue: string }
>(
  "studio/deleteMusic",
  async (musicId, { rejectWithValue }) => {
    try {
      const response = await customFetch.delete(
        `/api/studio/music/${musicId}`,
      );

      if (response.data?.status !== 1) {
        return rejectWithValue(
          response.data?.error?.message ||
            "Couldn't delete that track.",
        );
      }

      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error?.response?.data?.error?.message ||
          error?.message ||
          "Couldn't delete that track.",
      );
    }
  },
);

// ============================================================
// VIDEO
// ============================================================

export interface StudioOutputSizeParams {
  aspect: string;
  width: number;
  height: number;
  quality?: string; // default "WHATSAPP"
}

export const fetchStudioOutputSize = createAsyncThunk<
  any,
  StudioOutputSizeParams,
  { rejectValue: string }
>(
  "studio/fetchOutputSize",
  async ({ aspect, width, height, quality = "WHATSAPP" }, thunkAPI) => {
    try {
      const response = await customFetch.get("/api/studio/videos/output-size", {
        params: { aspect, width, height, quality },
      });

      return response.data?.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        getErrorMessage(error, "Failed to calculate video output size")
      );
    }
  }
);

export interface RenderStudioVideoParams {
  spec: any;
  overlays: Blob[];
}

export const renderStudioVideo = createAsyncThunk<
  any,
  RenderStudioVideoParams,
  { rejectValue: string }
>(
  "studio/renderVideo",
  async ({ spec, overlays }, thunkAPI) => {
    try {
      // "spec" as a form field (not in the URL, which has a size limit) and one PNG per
      // layer, in the same order as spec.overlays.
      const formData = new FormData();
      formData.append("spec", JSON.stringify(spec));
      (overlays || []).forEach((blob, i) =>
        formData.append("overlay", blob, `overlay${i}.png`),
      );

      const response = await customFetch.post(
        "/api/studio/videos/render",
        formData,
        {
          // Let the browser set multipart/form-data with its boundary.
          headers: {
            "Content-Type": undefined,
          },
        }
      );

      return response.data?.data;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        getErrorMessage(error, "Failed to render video")
      );
    }
  }
);

export const fetchStudioVideoJob = createAsyncThunk<
  any,
  number | string,
  { rejectValue: string }
>("studio/fetchVideoJob", async (id, thunkAPI) => {
  try {
    const response = await customFetch.get(`/api/studio/videos/jobs/${id}`);
    return response.data?.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      getErrorMessage(error, "Failed to fetch video job")
    );
  }
});

export const fetchStudioVideoJobs = createAsyncThunk<
  any,
  void,
  { rejectValue: string }
>("studio/fetchVideoJobs", async (_, thunkAPI) => {
  try {
    const response = await customFetch.get("/api/studio/videos/jobs", {
      params: { size: 10 },
    });

    let payload = response.data;

    if (typeof payload?.data === "string") {
      payload.data = JSON.parse(payload.data);
    }

    return payload.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      getErrorMessage(error, "Failed to fetch video jobs")
    );
  }
});

// ============================================================
// STUDIO V2 – FEATURES
// ============================================================

export const fetchStudioFeatures = createAsyncThunk<
  any,
  void,
  { rejectValue: string }
>("studio/fetchFeatures", async (_, thunkAPI) => {
  try {
    const response = await customFetch.get("/api/studio/features");
    return response.data?.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      getErrorMessage(error, "Failed to fetch studio features")
    );
  }
});

// ============================================================
// BRAND KIT
// ============================================================

export const fetchStudioBrandKit = createAsyncThunk<
  any,
  void,
  { rejectValue: string }
>("studio/fetchBrandKit", async (_, thunkAPI) => {
  try {
    const response = await customFetch.get("/api/studio/brand-kit");
    return response.data?.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      getErrorMessage(error, "Failed to fetch brand kit")
    );
  }
});

export const saveStudioBrandKit = createAsyncThunk<
  any,
  any,
  { rejectValue: string }
>("studio/saveBrandKit", async (body, thunkAPI) => {
  try {
    const response = await customFetch.put("/api/studio/brand-kit", body);
    return response.data?.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      getErrorMessage(error, "Failed to save brand kit")
    );
  }
});

// ============================================================
// DESIGN VERSIONS
// ============================================================

export const fetchStudioVersions = createAsyncThunk<
  any,
  number | string,
  { rejectValue: string }
>("studio/fetchVersions", async (id, thunkAPI) => {
  try {
    const response = await customFetch.get(
      `/api/studio/designs/${id}/versions`
    );
    return response.data?.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      getErrorMessage(error, "Failed to fetch design versions")
    );
  }
});

export interface SaveStudioVersionParams {
  id: number | string;
  label: string;
  thumbnailDataUrl: string;
}

export const saveStudioVersion = createAsyncThunk<
  any,
  SaveStudioVersionParams,
  { rejectValue: string }
>("studio/saveVersion", async ({ id, label, thumbnailDataUrl }, thunkAPI) => {
  try {
    const response = await customFetch.post(
      `/api/studio/designs/${id}/versions`,
      { label, thumbnailDataUrl }
    );
    return response.data?.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      getErrorMessage(error, "Failed to save design version")
    );
  }
});

export interface RestoreStudioVersionParams {
  id: number | string;
  versionId: number | string;
}

export const restoreStudioVersion = createAsyncThunk<
  any,
  RestoreStudioVersionParams,
  { rejectValue: string }
>("studio/restoreVersion", async ({ id, versionId }, thunkAPI) => {
  try {
    const response = await customFetch.post(
      `/api/studio/designs/${id}/versions/${versionId}/restore`
    );
    return response.data?.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      getErrorMessage(error, "Failed to restore design version")
    );
  }
});

// ============================================================
// STOCK PHOTOS
// ============================================================

export interface FetchStudioStockPhotosParams {
  q?: string;
  orientation?: string;
  page?: number;
}

export const fetchStudioStockPhotos = createAsyncThunk<
  any,
  FetchStudioStockPhotosParams | undefined,
  { rejectValue: string }
>("studio/fetchStockPhotos", async (params = {}, thunkAPI) => {
  const { q, orientation, page = 1 } = params;
  try {
    const response = await customFetch.get("/api/studio/stock/photos", {
      params: {
        page,
        ...(q ? { q } : {}),
        ...(orientation ? { orientation } : {}),
      },
    });
    return response.data?.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      getErrorMessage(error, "Failed to fetch stock photos")
    );
  }
});

export const importStudioStock = createAsyncThunk<
  any,
  string | number, // stock photo id
  { rejectValue: string }
>("studio/importStock", async (id, thunkAPI) => {
  try {
    const response = await customFetch.post("/api/studio/stock/import", {
      id,
    });
    return response.data?.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      getErrorMessage(error, "Failed to import stock photo")
    );
  }
});

// ============================================================
// AI
// ============================================================

export const removeStudioBackground = createAsyncThunk<
  any,
  any, // body (usually { url } or FormData depending on backend)
  { rejectValue: string }
>("studio/removeBackground", async (body, thunkAPI) => {
  try {
    const response = await customFetch.post(
      "/api/studio/ai/remove-background",
      body
    );
    return response.data?.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      getErrorMessage(error, "Failed to remove background")
    );
  }
});

/** Body: { galleryId?, mediaUrl?, scale: 2 | 4 }. Resolves to the bigger image (a new Gallery item). */
export const upscaleStudioImage = createAsyncThunk<
  MediaItem,
  { galleryId?: number | string; mediaUrl?: string; scale: 2 | 4 },
  { rejectValue: string }
>("studio/upscaleImage", async (body, thunkAPI) => {
  try {
    const response = await customFetch.post("/api/studio/ai/upscale", body);
    return response.data?.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      getErrorMessage(error, "The image couldn't be upscaled")
    );
  }
});

/** Body: { galleryId?, mediaUrl? }. Resolves to { subject, background } (two new Gallery items). */
export const magicLayersStudioImage = createAsyncThunk<
  { subject: MediaItem; background: MediaItem },
  { galleryId?: number | string; mediaUrl?: string },
  { rejectValue: string }
>("studio/magicLayers", async (body, thunkAPI) => {
  try {
    const response = await customFetch.post("/api/studio/ai/magic-layers", body);
    return response.data?.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      getErrorMessage(error, "The layers couldn't be made")
    );
  }
});

/** Body: { prompt, style }. Resolves to a transparent PNG Gallery item. */
export const generateStudioElement = createAsyncThunk<
  MediaItem,
  { prompt: string; style: string },
  { rejectValue: string }
>("studio/generateElement", async (body, thunkAPI) => {
  try {
    const response = await customFetch.post("/api/studio/ai/elements", body, {
      timeout: 180000,
    });
    return response.data?.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      getErrorMessage(error, "The element couldn't be made")
    );
  }
});

// ============================================================
// VOICEOVER
// ============================================================

export const uploadStudioVoiceover = createAsyncThunk<
  StudioVoiceover,
  Blob,
  { rejectValue: string }
>(
  "studio/uploadVoiceover",
  async (blob, thunkAPI) => {
    try {
      const mimeType = blob.type || "audio/webm";

      const extension =
        mimeType.includes("mpeg") || mimeType.includes("mp3")
          ? "mp3"
          : mimeType.includes("mp4") || mimeType.includes("m4a")
            ? "m4a"
            : mimeType.includes("ogg")
              ? "ogg"
              : mimeType.includes("wav")
                ? "wav"
                : "webm";

      const formData = new FormData();
      formData.append("file", blob, `voice.${extension}`);

      const response =
        await customFetch.post<StudioVoiceoverUploadResponse>(
          "/api/studio/voiceovers",
          formData,
          {
            headers: {
              "Content-Type": undefined,
            },
          }
        );

      const track = response.data.data;

      if (!track) {
        return thunkAPI.rejectWithValue(
          response.data.error?.message ??
            "The server did not return the uploaded voiceover."
        );
      }

      return track;
    } catch (error: unknown) {
      return thunkAPI.rejectWithValue(
        getErrorMessage(error, "Failed to upload voiceover")
      );
    }
  }
);

// ============================================================
// WHATSAPP PROFILE PICTURE
// ============================================================

export const setStudioProfilePicture = createAsyncThunk<
  any,
  Blob,
  { rejectValue: string }
>("studio/setProfilePicture", async (blob, thunkAPI) => {
  try {
    const formData = createFormData({
      file: {
        blob,
        name: "profile.jpg",
      },
    });

    const response = await customFetch.post(
      "/api/whatsapp/profile/picture",
      formData
    );

    return response.data?.data;
  } catch (error: any) {
    return thunkAPI.rejectWithValue(
      getErrorMessage(error, "Failed to update profile picture")
    );
  }
});


const objectUrls = new Map<string, Promise<string>>();

/**
 * A stored image as a same-origin object URL, so it can be drawn on a canvas and exported
 * (S3 files go through GET /api/studio/media). Cached for the session.
 */
export function assetObjectUrl(url: string): Promise<string> {
  if (!url || url.startsWith("blob:") || url.startsWith("data:")) return Promise.resolve(url);
  const cached = objectUrls.get(url);
  if (cached) return cached;

  const pending = customFetch
    .get("/api/studio/media", { params: { url }, responseType: "blob" })
    .then((res) => URL.createObjectURL(res.data as Blob))
    .catch(() => {
      objectUrls.delete(url); // allow a retry
      throw new Error("Could not load the image");
    });

  objectUrls.set(url, pending);
  return pending;
}